// Factory presets — small starter set of sounds shipped with the synth.
// Each is a complete `Preset` snapshot; `Init` is the "reset to neutral
// defaults" preset that matches engine + UI initial values.

import { PRESET_VERSION, type Preset } from "./preset.ts";

/** Neutral defaults. Loading "init" returns every control to its
 *  factory-fresh state — exactly what the synth boots up with. */
export const INIT_PRESET: Preset = {
  version: PRESET_VERSION,
  name: "init",
  osc1: { shape: 0, octave: 0, detuneCents: 0, level: 0.8, enabled: true },
  osc2: { shape: 0, octave: 0, detuneCents: 0, level: 0, enabled: false },
  filter: { cutoffHz: 1500, resonance: 0.707, mode: "lp", drive: 0 },
  ampEnv: { attackMs: 5, decayMs: 120, sustain: 0.6, releaseMs: 200 },
  modEnv: {
    attackMs: 5,
    decayMs: 200,
    sustain: 0,
    releaseMs: 200,
    amount: 0,
    destination: "none",
  },
  lfo: {
    shape: "sine",
    rateHz: 1,
    rateSyncIndex: 2,
    sync: false,
    amount: 0,
    destination: "none",
  },
  bpm: 120,
  eq: { lowGainDb: 0, midFreqHz: 1000, midGainDb: 0, midQ: 0.707, highGainDb: 0 },
  distortion: { driveDb: 0, mix: 0, curve: "soft" },
  delay: {
    timeLMs: 380,
    timeRMs: 520,
    feedback: 0.35,
    crossFeedback: 0.15,
    toneHz: 6000,
    mix: 0,
  },
  reverb: { size: 0.55, damping: 0.4, mix: 0 },
  master: { gain: 0.7 },
  fxOrder: ["eq", "distortion", "delay", "reverb"],
};

/** Sub bass — sine osc one octave down, lowpass with low cutoff, snappy
 *  envelope, no FX. Designed to demonstrate punchy low-end. */
export const SUB_BASS_PRESET: Preset = {
  ...INIT_PRESET,
  name: "sub bass",
  osc1: { shape: 0, octave: -1, detuneCents: 0, level: 0.9, enabled: true },
  osc2: { shape: 0, octave: -2, detuneCents: -7, level: 0.4, enabled: true },
  filter: { cutoffHz: 420, resonance: 1.2, mode: "lp", drive: 0.25 },
  ampEnv: { attackMs: 2, decayMs: 180, sustain: 0.55, releaseMs: 140 },
  master: { gain: 0.55 },
};

/** Lead saw — detuned saw stack through a resonant bandpass with a long
 *  release and delay/reverb sprinkled in. Big, expressive solo voice. */
export const LEAD_SAW_PRESET: Preset = {
  ...INIT_PRESET,
  name: "lead saw",
  osc1: { shape: 1, octave: 0, detuneCents: -12, level: 0.7, enabled: true },
  osc2: { shape: 1, octave: 0, detuneCents: 12, level: 0.7, enabled: true },
  filter: { cutoffHz: 3500, resonance: 1.8, mode: "bp", drive: 0.15 },
  ampEnv: { attackMs: 10, decayMs: 350, sustain: 0.7, releaseMs: 450 },
  modEnv: {
    attackMs: 60,
    decayMs: 400,
    sustain: 0.2,
    releaseMs: 250,
    amount: 0.55,
    destination: "filter.cutoff",
  },
  lfo: {
    shape: "sine",
    rateHz: 5,
    rateSyncIndex: 2,
    sync: false,
    amount: 0.1,
    destination: "pitch",
  },
  delay: {
    timeLMs: 280,
    timeRMs: 420,
    feedback: 0.45,
    crossFeedback: 0.25,
    toneHz: 5000,
    mix: 0.28,
  },
  reverb: { size: 0.65, damping: 0.45, mix: 0.22 },
  master: { gain: 0.6 },
};

/** Soft pad — slow square pad with long attack/release, dark LP filter,
 *  slow LFO opening the cutoff, big airy reverb. */
export const SOFT_PAD_PRESET: Preset = {
  ...INIT_PRESET,
  name: "soft pad",
  osc1: { shape: 2 / 3, octave: 0, detuneCents: -8, level: 0.55, enabled: true },
  osc2: { shape: 2 / 3, octave: 1, detuneCents: 8, level: 0.45, enabled: true },
  filter: { cutoffHz: 1100, resonance: 0.85, mode: "lp", drive: 0.05 },
  ampEnv: { attackMs: 800, decayMs: 600, sustain: 0.85, releaseMs: 1800 },
  modEnv: {
    attackMs: 1200,
    decayMs: 1500,
    sustain: 1,
    releaseMs: 1800,
    amount: 0.35,
    destination: "filter.cutoff",
  },
  lfo: {
    shape: "sine",
    rateHz: 0.35,
    rateSyncIndex: 2,
    sync: false,
    amount: 0.18,
    destination: "filter.cutoff",
  },
  reverb: { size: 0.78, damping: 0.3, mix: 0.45 },
  master: { gain: 0.55 },
};

/** Ordered list — preset bar shows them in this sequence. `init`
 *  always first so it's easy to reset to defaults. */
export const FACTORY_PRESETS: Preset[] = [
  INIT_PRESET,
  SUB_BASS_PRESET,
  LEAD_SAW_PRESET,
  SOFT_PAD_PRESET,
];
